import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, Plus, Edit, Trash2, Search, Filter, RefreshCw,
  Globe, Check, X, ChevronLeft, ChevronRight, CheckCircle, XCircle
} from 'lucide-react';
import { adminService } from '../services/adminService';
import { Bank } from '../types';
import { useToast } from '../contexts/ToastContext';
import './Banks.css';

interface BankFormData {
  name: string;
  code: string;
  country_code: string;
  logo_url: string;
  is_active: boolean;
}

const emptyForm: BankFormData = {
  name: '',
  code: '',
  country_code: 'NG',
  logo_url: '',
  is_active: true,
};

const COUNTRY_OPTIONS = [
  { value: '', label: 'All Countries' },
  { value: 'NG', label: 'Nigeria (NG)' },
  { value: 'US', label: 'United States (US)' },
  { value: 'GB', label: 'United Kingdom (GB)' },
];

const Banks: React.FC = () => {
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<boolean | null>(null);
  const [page, setPage] = useState(0);
  const [totalBanks, setTotalBanks] = useState(0);
  const pageSize = 20;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBank, setEditingBank] = useState<Bank | null>(null);
  const [deletingBank, setDeletingBank] = useState<Bank | null>(null);
  const [formData, setFormData] = useState<BankFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const toast = useToast();

  const loadBanks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getBanks({
        search: search || undefined,
        country_code: countryFilter || undefined,
        is_active: statusFilter !== null ? statusFilter : undefined,
        skip: page * pageSize,
        limit: pageSize,
      });

      const bankList: Bank[] = Array.isArray(data) ? data : (data?.banks ?? data?.items ?? data?.data ?? []);
      setBanks(bankList);
      setTotalBanks(
        data?.total ?? data?.count ??
        (bankList.length >= pageSize ? (page + 1) * pageSize + pageSize : page * pageSize + bankList.length)
      );
    } catch (error: any) {
      console.error('Failed to load banks:', error);
      toast.error('Load Failed', error?.response?.data?.detail || 'Could not fetch banks');
      setBanks([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, countryFilter, statusFilter, page]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      loadBanks();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [loadBanks]);

  // Stats
  const activeBanks = banks.filter(b => b.is_active).length;
  const inactiveBanks = banks.filter(b => !b.is_active).length;
  const countriesCovered = new Set(banks.map(b => b.country_code)).size;

  // Create
  const handleOpenCreate = () => {
    setFormData(emptyForm);
    setShowCreateModal(true);
  };

  const handleCloseCreate = () => {
    setShowCreateModal(false);
    setFormData(emptyForm);
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.code.trim() || !formData.country_code) {
      toast.warning('Validation', 'Name, code, and country are required');
      return;
    }
    try {
      setSaving(true);
      await adminService.createBank({
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        country_code: formData.country_code,
        logo_url: formData.logo_url.trim() || undefined,
      });
      toast.success('Bank Created', `${formData.name} has been added`);
      handleCloseCreate();
      await loadBanks();
    } catch (error: any) {
      toast.error('Create Failed', error?.response?.data?.detail || 'Could not create bank');
    } finally {
      setSaving(false);
    }
  };

  // Edit
  const handleOpenEdit = (bank: Bank) => {
    setEditingBank(bank);
    setFormData({
      name: bank.name,
      code: bank.code,
      country_code: bank.country_code,
      logo_url: bank.logo_url || '',
      is_active: bank.is_active,
    });
  };

  const handleCloseEdit = () => {
    setEditingBank(null);
    setFormData(emptyForm);
  };

  const handleUpdate = async () => {
    if (!editingBank) return;
    if (!formData.name.trim() || !formData.code.trim() || !formData.country_code) {
      toast.warning('Validation', 'Name, code, and country are required');
      return;
    }
    try {
      setSaving(true);
      await adminService.updateBank(editingBank.id, {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        country_code: formData.country_code,
        logo_url: formData.logo_url.trim() || undefined,
        is_active: formData.is_active,
      });
      toast.success('Bank Updated', `${formData.name} has been updated`);
      handleCloseEdit();
      await loadBanks();
    } catch (error: any) {
      toast.error('Update Failed', error?.response?.data?.detail || 'Could not update bank');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleOpenDelete = (bank: Bank) => {
    setDeletingBank(bank);
  };

  const handleCloseDelete = () => {
    setDeletingBank(null);
  };

  const handleDelete = async () => {
    if (!deletingBank) return;
    try {
      setSaving(true);
      await adminService.deleteBank(deletingBank.id);
      toast.success('Bank Deleted', `${deletingBank.name} has been removed`);
      handleCloseDelete();
      await loadBanks();
    } catch (error: any) {
      toast.error('Delete Failed', error?.response?.data?.detail || 'Could not delete bank');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="banks-page">
      {/* Header */}
      <div className="banks-header">
        <div className="header-content">
          <h1><Building2 size={28} /> Bank Management</h1>
          <p className="header-subtitle">Manage supported banks and financial institutions</p>
        </div>
        <div className="header-actions">
          <button onClick={loadBanks} className="refresh-btn" disabled={loading}>
            <RefreshCw className={loading ? 'spin' : ''} size={18} />
            Refresh
          </button>
          <button onClick={handleOpenCreate} className="create-btn">
            <Plus size={18} />
            Add Bank
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="banks-summary">
        <div className="summary-item">
          <span className="summary-value">{totalBanks}</span>
          <span className="summary-label">Total Banks</span>
        </div>
        <div className="summary-item summary-active">
          <span className="summary-value">{activeBanks}</span>
          <span className="summary-label">Active Banks</span>
        </div>
        <div className="summary-item summary-countries">
          <span className="summary-value">{countriesCovered}</span>
          <span className="summary-label">Countries Covered</span>
        </div>
        <div className="summary-item summary-inactive">
          <span className="summary-value">{inactiveBanks}</span>
          <span className="summary-label">Inactive Banks</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name or code..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          />
          {search && (
            <button className="clear-search" onClick={() => { setSearch(''); setPage(0); }}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="filter-group">
          <Filter size={16} />
          <select
            value={countryFilter}
            onChange={(e) => { setCountryFilter(e.target.value); setPage(0); }}
          >
            {COUNTRY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={statusFilter === null ? '' : statusFilter.toString()}
            onChange={(e) => {
              const val = e.target.value;
              setStatusFilter(val === '' ? null : val === 'true');
              setPage(0);
            }}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="banks-table-container">
        {loading ? (
          <div className="loading-state">
            <RefreshCw className="spin" size={32} />
            <span>Loading banks...</span>
          </div>
        ) : (
          <>
            <table className="banks-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {banks.map((bank) => (
                  <tr key={bank.id}>
                    <td className="id-cell">#{bank.id}</td>
                    <td>
                      <div className="bank-name-cell">
                        {bank.logo_url ? (
                          <img
                            src={bank.logo_url}
                            alt={bank.name}
                            className="bank-logo"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div className="bank-logo-placeholder">
                            <Building2 size={16} />
                          </div>
                        )}
                        <span className="bank-name">{bank.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="code-badge">{bank.code}</span>
                    </td>
                    <td>
                      <span className="country-badge">
                        <Globe size={14} /> {bank.country_code}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${bank.is_active ? 'active' : 'inactive'}`}>
                        {bank.is_active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {bank.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleOpenEdit(bank)}
                          className="action-btn edit"
                          title="Edit bank"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleOpenDelete(bank)}
                          className="action-btn delete"
                          title="Delete bank"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {banks.length === 0 && (
              <div className="no-data">
                <Building2 size={48} />
                <h3>No banks found</h3>
                <p>Try adjusting your search or filters, or add a new bank</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination */}
      {banks.length > 0 && (
        <div className="pagination">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="page-btn"
          >
            <ChevronLeft size={18} /> Previous
          </button>
          <span className="page-info">
            Page {page + 1} • Showing {page * pageSize + 1} - {page * pageSize + banks.length}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={banks.length < pageSize}
            className="page-btn"
          >
            Next <ChevronRight size={18} />
          </button>
        </div>
      )}

      {/* Create Bank Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCloseCreate}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Bank</h3>
              <button className="modal-close" onClick={handleCloseCreate}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Bank Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input"
                  placeholder="e.g. First Bank of Nigeria"
                />
              </div>
              <div className="form-group">
                <label>Bank Code <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="form-input"
                  placeholder="e.g. 011"
                />
              </div>
              <div className="form-group">
                <label>Country <span className="required">*</span></label>
                <select
                  value={formData.country_code}
                  onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                  className="form-input"
                >
                  {COUNTRY_OPTIONS.filter(c => c.value !== '').map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Logo URL <span className="optional">(optional)</span></label>
                <input
                  type="text"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  className="form-input"
                  placeholder="https://example.com/logo.png"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseCreate} className="cancel-btn" disabled={saving}>Cancel</button>
              <button onClick={handleCreate} className="save-btn" disabled={saving}>
                {saving ? 'Creating...' : 'Create Bank'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Bank Modal */}
      {editingBank && (
        <div className="modal-overlay" onClick={handleCloseEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Bank</h3>
              <button className="modal-close" onClick={handleCloseEdit}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Bank Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Bank Code <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Country <span className="required">*</span></label>
                <select
                  value={formData.country_code}
                  onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                  className="form-input"
                >
                  {COUNTRY_OPTIONS.filter(c => c.value !== '').map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Logo URL <span className="optional">(optional)</span></label>
                <input
                  type="text"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  className="form-input"
                  placeholder="https://example.com/logo.png"
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <div className="toggle-group">
                  <button
                    type="button"
                    className={`toggle-btn ${formData.is_active ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, is_active: true })}
                  >
                    <Check size={14} /> Active
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${!formData.is_active ? 'inactive' : ''}`}
                    onClick={() => setFormData({ ...formData, is_active: false })}
                  >
                    <X size={14} /> Inactive
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseEdit} className="cancel-btn" disabled={saving}>Cancel</button>
              <button onClick={handleUpdate} className="save-btn" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingBank && (
        <div className="modal-overlay" onClick={handleCloseDelete}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Bank</h3>
              <button className="modal-close" onClick={handleCloseDelete}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <div className="delete-icon">
                  <Trash2 size={32} />
                </div>
                <p>
                  Are you sure you want to delete <strong>{deletingBank.name}</strong> ({deletingBank.code})?
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseDelete} className="cancel-btn" disabled={saving}>Cancel</button>
              <button onClick={handleDelete} className="delete-confirm-btn" disabled={saving}>
                {saving ? 'Deleting...' : 'Delete Bank'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Banks;
